import type { ComponentProps, ReactNode } from "react"
import { forwardRef } from "react"

import { cn } from "@productlane/lib/src/cn"
import { DropdownMenu } from "@productlane/ui"
import { inputVariants } from "@productlane/ui/src/components/form/input/input-variants"

import { HIGHLIGHT_RING_WITHIN } from "./highlight-metrics"
import { PLAIN_TEXT_FIELD } from "./highlighted-field"
import { OptionItems } from "./option-select"
import { TOKEN_TINT } from "./policy-token-highlight"

export interface ConnectorChoice {
	id: string
	title: string
}

interface Props extends Omit<ComponentProps<"input">, "children"> {
	name: string
	sectionId: string
	connectorTitle: string
	hint: ReactNode
	/** HTTP connectors the chip can be pointed at. MCP ones serve no values. */
	choices: readonly ConnectorChoice[]
	onPickConnector: (sectionId: string) => void
}

/**
 * `http.[connector].key`, with the 36-character id standing in as a chip. The
 * input holds the key alone, so nothing has to keep a caret and an overlay in step
 * over text the field is not really showing.
 */
export const ChippedRefField = forwardRef<HTMLInputElement, Props>(
	(
		{
			name,
			sectionId,
			connectorTitle,
			hint,
			choices,
			onPickConnector,
			...props
		},
		ref,
	) => (
		<div className="flex w-full flex-col">
			<div
				className={cn(
					inputVariants(),
					HIGHLIGHT_RING_WITHIN,
					"items-center font-mono",
				)}
			>
				<span className={cn("flex-none", TOKEN_TINT.namespace)}>http</span>
				<span className={cn("flex-none", TOKEN_TINT.separator)}>.</span>
				<DropdownMenu
					contentProps={{ align: "start" }}
					trigger={
						<button
							type="button"
							className={cn(
								"max-w-48 flex-none cursor-default truncate",
								TOKEN_TINT.sectionId,
							)}
							aria-label={`Reads from ${connectorTitle}. Pick a different connector.`}
						>
							{connectorTitle}
						</button>
					}
				>
					<OptionItems
						value={sectionId}
						options={choices.map((choice) => ({
							value: choice.id,
							label: choice.title,
						}))}
						onSelect={onPickConnector}
						// Reads with the suggestion rows below it rather than a size larger.
						itemClassName="text-xs"
					/>
				</DropdownMenu>
				<span className={cn("flex-none", TOKEN_TINT.separator)}>.</span>
				<input
					{...props}
					{...PLAIN_TEXT_FIELD}
					ref={ref}
					name={name}
					id={name}
					placeholder="billingAccountId"
					className={cn(
						"min-w-0 flex-1 bg-transparent outline-none placeholder:text-tertiary",
						TOKEN_TINT.key,
					)}
				/>
			</div>
			{hint ? <p className="mt-1.5 text-xs text-tertiary">{hint}</p> : null}
		</div>
	),
)
